// Photo annotation (Owner decision list 2026-10-19 item 22): arrows, circles and boxes drawn on a photo to
// point at the defect. Stored as shapes on the photo record in coordinates relative to the photo (0…1), so
// the original image is never altered; the viewer draws them over the photo and the report prints them on it.
export type AnnotationKind = 'arrow' | 'circle' | 'box';

export interface PhotoAnnotation {
  kind: AnnotationKind;
  // From (x1, y1) to (x2, y2): an arrow points from the first point to the second; a circle or a box
  // fills the rectangle the two points span.
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export const MAX_ANNOTATIONS = 20;
export const ANNOTATION_COLOR = '#ff1f1f';
export const ANNOTATION_HALO = '#ffffff';

const clamp = (n: number) => Math.min(1, Math.max(0, n));

export function isPhotoAnnotation(value: unknown): value is PhotoAnnotation {
  const a = value as PhotoAnnotation;
  return (
    typeof a === 'object' &&
    a !== null &&
    (a.kind === 'arrow' || a.kind === 'circle' || a.kind === 'box') &&
    [a.x1, a.y1, a.x2, a.y2].every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1)
  );
}

export function annotationsOf(value: unknown): PhotoAnnotation[] {
  return Array.isArray(value) ? value.filter(isPhotoAnnotation).slice(0, MAX_ANNOTATIONS) : [];
}

export function normalizedAnnotation(kind: AnnotationKind, x1: number, y1: number, x2: number, y2: number): PhotoAnnotation {
  return { kind, x1: clamp(x1), y1: clamp(y1), x2: clamp(x2), y2: clamp(y2) };
}

// Too small to be a deliberate mark (a tap rather than a drag): dropped.
export function isMeaningfulAnnotation(a: PhotoAnnotation): boolean {
  return Math.hypot(a.x2 - a.x1, a.y2 - a.y1) >= 0.03;
}

export type AnnotationPrimitive =
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number }
  | { type: 'polygon'; points: [number, number][] }
  | { type: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { type: 'rect'; x: number; y: number; w: number; h: number };

// The shapes in pixels of a width × height image, and the stroke width that reads at that size.
export function annotationPrimitives(annotations: readonly PhotoAnnotation[], width: number, height: number): { stroke: number; shapes: AnnotationPrimitive[] } {
  const stroke = Math.max(2, Math.round(Math.max(width, height) * 0.008));
  const shapes: AnnotationPrimitive[] = [];
  for (const a of annotations) {
    const x1 = a.x1 * width;
    const y1 = a.y1 * height;
    const x2 = a.x2 * width;
    const y2 = a.y2 * height;
    if (a.kind === 'arrow') {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const head = Math.max(stroke * 4, Math.min(Math.hypot(x2 - x1, y2 - y1) * 0.35, stroke * 7));
      const spread = Math.PI / 7;
      // The shaft stops at the base of the head so the point stays sharp.
      const bx = x2 - Math.cos(angle) * head * 0.8;
      const by = y2 - Math.sin(angle) * head * 0.8;
      shapes.push({ type: 'line', x1, y1, x2: bx, y2: by });
      shapes.push({
        type: 'polygon',
        points: [
          [x2, y2],
          [x2 - Math.cos(angle - spread) * head, y2 - Math.sin(angle - spread) * head],
          [x2 - Math.cos(angle + spread) * head, y2 - Math.sin(angle + spread) * head]
        ]
      });
    } else if (a.kind === 'circle') {
      shapes.push({ type: 'ellipse', cx: (x1 + x2) / 2, cy: (y1 + y2) / 2, rx: Math.abs(x2 - x1) / 2, ry: Math.abs(y2 - y1) / 2 });
    } else {
      shapes.push({ type: 'rect', x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) });
    }
  }
  return { stroke, shapes };
}

function shapeSvg(shape: AnnotationPrimitive, stroke: string, width: number, fill: boolean): string {
  const common = `stroke="${stroke}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"`;
  switch (shape.type) {
    case 'line':
      return `<line x1="${shape.x1.toFixed(1)}" y1="${shape.y1.toFixed(1)}" x2="${shape.x2.toFixed(1)}" y2="${shape.y2.toFixed(1)}" ${common}/>`;
    case 'polygon':
      return `<polygon points="${shape.points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}" fill="${fill ? stroke : 'none'}" ${common}/>`;
    case 'ellipse':
      return `<ellipse cx="${shape.cx.toFixed(1)}" cy="${shape.cy.toFixed(1)}" rx="${shape.rx.toFixed(1)}" ry="${shape.ry.toFixed(1)}" fill="none" ${common}/>`;
    case 'rect':
      return `<rect x="${shape.x.toFixed(1)}" y="${shape.y.toFixed(1)}" width="${shape.w.toFixed(1)}" height="${shape.h.toFixed(1)}" fill="none" ${common}/>`;
  }
}

// The marks as SVG content for a viewBox of width × height: a white halo under the red so they read on
// dark and light surfaces alike.
export function annotationSvgContent(annotations: readonly PhotoAnnotation[], width: number, height: number): string {
  const { stroke, shapes } = annotationPrimitives(annotations, width, height);
  return [...shapes.map((s) => shapeSvg(s, ANNOTATION_HALO, stroke * 2, true)), ...shapes.map((s) => shapeSvg(s, ANNOTATION_COLOR, stroke, true))].join('');
}

// The same marks drawn onto a 2D canvas that already holds the photo (the report's print copy).
export function drawAnnotations(ctx: CanvasRenderingContext2D, annotations: readonly PhotoAnnotation[], width: number, height: number): void {
  const { stroke, shapes } = annotationPrimitives(annotations, width, height);
  for (const [color, lineWidth] of [
    [ANNOTATION_HALO, stroke * 2],
    [ANNOTATION_COLOR, stroke]
  ] as const) {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const shape of shapes) {
      ctx.beginPath();
      if (shape.type === 'line') {
        ctx.moveTo(shape.x1, shape.y1);
        ctx.lineTo(shape.x2, shape.y2);
        ctx.stroke();
      } else if (shape.type === 'polygon') {
        shape.points.forEach(([x, y], index) => (index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else if (shape.type === 'ellipse') {
        ctx.ellipse(shape.cx, shape.cy, Math.max(1, shape.rx), Math.max(1, shape.ry), 0, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(shape.x, shape.y, shape.w, shape.h);
      }
    }
  }
}
